import React, { useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Box,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PrintIcon from "@mui/icons-material/Print";
import { useTranslation } from "react-i18next";
import { triggerSonetSettlement } from "app/services/api/management";

const SonetSettlementButton = ({ deviceUuid, beDomain, customerId, disabled = false }) => {
  const { t } = useTranslation();
  const printRef = useRef(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleClick = () => setConfirmOpen(true);
  const handleCancel = () => setConfirmOpen(false);

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setLoading(true);
    try {
      const data = await triggerSonetSettlement(deviceUuid, beDomain, customerId);
      setResult({ ok: true, data });
    } catch (err) {
      setResult({
        ok: false,
        error: err?.response?.data?.error || { message: err?.message || t("sonetSettlement.result.unknownError") },
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!printRef.current) return;
    const content = printRef.current.innerHTML;
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>${t("sonetSettlement.printTitle")}</title>
      <style>body{font-family:monospace;padding:24px;font-size:13px;}h2{margin-bottom:8px;}table{border-collapse:collapse;width:100%;}td{padding:4px 8px;vertical-align:top;}td:first-child{font-weight:bold;white-space:nowrap;min-width:160px;}</style>
      </head><body>${content}</body></html>
    `);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const resultData = result?.data;
  const success = resultData?.success;
  const transactionRc = resultData?.transactionRc;
  const rawFields = resultData?.rawFields;

  return (
    <>
      <Button
        variant="outlined"
        color="warning"
        size="small"
        disabled={disabled || loading}
        onClick={handleClick}
        sx={{ boxShadow: "none", height: "38px", minWidth: 120 }}
      >
        {loading ? (
          <CircularProgress size={16} sx={{ color: "inherit" }} />
        ) : (
          t("sonetSettlement.button")
        )}
      </Button>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onClose={handleCancel} maxWidth="xs" fullWidth>
        <DialogTitle>{t("sonetSettlement.confirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("sonetSettlement.confirmText")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="inherit" sx={{ minHeight: 44 }}>
            {t("buttons.cancel")}
          </Button>
          <Button onClick={handleConfirm} variant="contained" color="warning" autoFocus sx={{ minHeight: 44 }}>
            {t("buttons.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Result dialog */}
      <Dialog open={result !== null} onClose={() => setResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {result?.ok && success ? (
            <CheckCircleOutlineIcon color="success" />
          ) : (
            <ErrorOutlineIcon color="error" />
          )}
          {result?.ok && success
            ? t("sonetSettlement.result.successTitle")
            : t("sonetSettlement.result.failureTitle")}
        </DialogTitle>
        <DialogContent>
          <Box ref={printRef}>
            <Typography variant="h6" gutterBottom>
              {t("sonetSettlement.printTitle")}
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            {result?.ok ? (
              <table>
                <tbody>
                  <tr>
                    <td>{t("sonetSettlement.result.status")}</td>
                    <td>
                      <Typography color={success ? "success.main" : "error.main"} fontWeight={600} component="span">
                        {success ? t("sonetSettlement.result.ok") : t("sonetSettlement.result.failed")}
                      </Typography>
                    </td>
                  </tr>
                  <tr>
                    <td>{t("sonetSettlement.result.transactionRc")}</td>
                    <td>{transactionRc ?? "—"}</td>
                  </tr>
                  {Array.isArray(rawFields) && rawFields.length > 0 && (
                    <tr>
                      <td>{t("sonetSettlement.result.rawFields")}</td>
                      <td>
                        <Box component="pre" sx={{ m: 0, fontSize: 11, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                          {rawFields.join("\n")}
                        </Box>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <Typography color="error">
                {result?.error?.message || result?.data?.error?.message || t("sonetSettlement.result.unknownError")}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePrint} startIcon={<PrintIcon />} color="inherit">
            {t("sonetSettlement.printButton")}
          </Button>
          <Button onClick={() => setResult(null)} variant="contained">
            {t("buttons.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SonetSettlementButton;
